import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchAuditLog, type AuditLogEntry } from '@/lib/api';

const ACTION_LABELS: Record<string, { label: string; emoji: string }> = {
  reply_sent: { label: 'Sent reply', emoji: '\u{1F4AC}' },
  resolve: { label: 'Marked resolved', emoji: '✅' },
  review_request: { label: 'Sent review request', emoji: '⭐' },
  owner_profile_update: { label: 'Updated owner profile', emoji: '\u{1F464}' },
  quick_replies_update: { label: 'Updated quick replies', emoji: '\u{1F4DD}' },
  export_data: { label: 'Exported data', emoji: '\u{1F4E6}' },
  export_reports: { label: 'Exported reports', emoji: '\u{1F4CA}' },
  mark_all_read: { label: 'Marked all as read', emoji: '\u{1F4ED}' },
  opt_out_detected: { label: 'Customer opted out', emoji: '\u{1F6AB}' },
};

function labelFor(action: string): { label: string; emoji: string } {
  return ACTION_LABELS[action] ?? { label: action.replace(/_/g, ' '), emoji: '\u{1F4CC}' };
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  return d.toISOString().slice(0, 10);
}

function formatDayHeader(key: string): string {
  const today = new Date().toISOString().slice(0, 10);
  if (key === today) return 'Today';
  const yesterday = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
  if (key === yesterday) return 'Yesterday';
  return new Date(key).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * AuditLog — read-only log of owner-side actions for the active tenant.
 * Pulls newest-first from /pwa/audit/log and groups by day. If the Worker
 * polish endpoints aren't deployed yet, the helper returns an empty page
 * and the empty state renders cleanly.
 */
export function AuditLog() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAuditLog({ slug, limit: 100 })
      .then((page) => {
        if (cancelled) return;
        setEntries(page.entries);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load activity');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Group by day key, preserving newest-first order.
  const grouped = useMemo(() => {
    const map = new Map<string, AuditLogEntry[]>();
    for (const e of entries) {
      const k = dayKey(e.timestamp_iso);
      const list = map.get(k) ?? [];
      list.push(e);
      map.set(k, list);
    }
    return Array.from(map.entries());
  }, [entries]);

  return (
    <div
      className="h-full min-h-0 overflow-y-auto px-4 pb-12"
      style={{
        WebkitOverflowScrolling: 'touch',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div className="flex items-center gap-3 px-1 pb-3 pt-2">
        <button
          type="button"
          onClick={() => navigate(`/${slug}/settings`)}
          aria-label="Back to settings"
          className="ss-press flex h-9 w-9 items-center justify-center rounded-full bg-ink-100 dark:bg-[#1E293B] text-ink-700 dark:text-[#94A3B8]"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h1 className="text-2xl font-bold tracking-tight text-ink-900 dark:text-[#E2E8F0]">
          Activity log
        </h1>
      </div>

      {loading ? (
        <div className="px-2 py-12 text-center text-sm text-ink-500 dark:text-[#94A3B8]">
          Loading activity…
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      ) : entries.length === 0 ? (
        <div className="mx-1 mt-6 rounded-2xl border border-ink-100 dark:border-[#334155] bg-white dark:bg-[#1E293B] px-5 py-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-ink-100 dark:bg-[#0F172A] text-2xl">
            {'\u{1F4D6}'}
          </div>
          <div className="text-[15px] font-semibold text-ink-900 dark:text-[#E2E8F0]">
            Nothing here yet
          </div>
          <p className="mt-1 text-sm text-ink-500 dark:text-[#94A3B8]">
            As you work in the app, your actions will show up here for audit and review.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([day, group]) => (
            <div key={day}>
              <div className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-ink-500 dark:text-[#94A3B8]">
                {formatDayHeader(day)}
              </div>
              <ul className="overflow-hidden rounded-2xl border border-ink-100 dark:border-[#334155] bg-white dark:bg-[#1E293B]">
                {group.map((entry) => {
                  const meta = labelFor(entry.action);
                  return (
                    <li
                      key={entry.id}
                      className="flex items-start gap-3 border-b border-ink-100 dark:border-[#334155] px-4 py-3 last:border-b-0"
                    >
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-ink-100 dark:bg-[#0F172A] text-base">
                        {meta.emoji}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-ink-900 dark:text-[#E2E8F0]">
                          {meta.label}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-ink-500 dark:text-[#94A3B8]">
                          {entry.actor}
                          {entry.contact_id ? (
                            <span> &middot; contact {entry.contact_id.slice(0, 8)}</span>
                          ) : null}
                        </div>
                      </div>
                      <div className="text-xs text-ink-400 dark:text-[#64748B]">
                        {formatTime(entry.timestamp_iso)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
